import { Injectable } from '@angular/core';
import { GridInput } from '@it2go/data-grid';
import { IdInput } from '@libs/types/src/common/input-type/id.input';
import { OkObject } from '@it2go/types';
import { SocialPaymentCreateInput } from '@libs/types/src/services/input-type/social/social-payment/social-payment.create-input';
import { SocialPaymentUpdateInput } from '@libs/types/src/services/input-type/social/social-payment/social-payment.update-input';
import {
    SocialPaymentNextSequenceNumberInput,
} from '@libs/types/src/services/input-type/social/social-payment/social-payment.next-sequence-number-input';
import {
    SocialPaymentDeficitSummaryObject,
    SocialPaymentGridObject,
    SocialPaymentObject,
} from '@libs/types/src/services/object-type/folder/social-payment.object';
import { GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import { filterFrag } from './types/filter.types';
import {
    SocialPaymentFromFinancialDepositInput,
} from '@libs/types/src/services/input-type/social/social-payment/social-payment-from-financial-deposit.input';
import {
    SocialPaymentPreviousBillingBalanceInput,
} from '@libs/types/src/services/input-type/social/social-payment/social-payment-previous-billing-balance.input';

@Injectable({ providedIn: 'root' })
export class SocialPaymentGqlServiceV2 extends GqlServiceV2 {

    public getSocialPayments(input: GridInput | null, frag: string) {
        return this.query<SocialPaymentGridObject>(
            `
                query($input: GridInput!) {
                    socialPayment {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createSocialPayment(input: SocialPaymentCreateInput, frag: string) {
        return this.mutate<SocialPaymentObject>(
            `
                mutation($input: SocialPaymentCreateInput!) {
                    socialPayment {
                        create(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createSocialPayments(input: SocialPaymentCreateInput[], frag: string = 'ok') {
        return this.mutate<SocialPaymentObject>(
            `
                mutation($input: [SocialPaymentCreateInput!]!) {
                    socialPayment {
                        createBatch(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getSocialPaymentNextSequenceNumber(input: SocialPaymentNextSequenceNumberInput): Promise<number> {
        return this.query<number>(
            `
                query($input: SocialPaymentNextSequenceNumberInput!) {
                    socialPayment {
                        nextSequenceNumber(input: $input)
                    }
                }
            `,
            { input },
        );
    }

    public getSocialPaymentDeficitSummary(input: IdInput): Promise<SocialPaymentDeficitSummaryObject> {
        return this.query<SocialPaymentDeficitSummaryObject>(
            `
                query($input: IdInput!) {
                    socialPayment {
                        deficitSummary(input: $input) {
                            debt
                            payments
                            difference
                        }
                    }
                }
            `,
            { input },
        );
    }

    public paySocialPaymentFromFinancialDeposit(input: SocialPaymentFromFinancialDepositInput, frag: string) {
        return this.mutate<SocialPaymentObject>(
            `
                mutation($input: SocialPaymentFromFinancialDepositInput!) {
                    socialPayment {
                        payFromFinancialDeposit(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public addSocialPaymentPreviousBillingBalance(input: SocialPaymentPreviousBillingBalanceInput, frag: string) {
        return this.mutate<SocialPaymentObject[]>(
            `
                mutation($input: SocialPaymentPreviousBillingBalanceInput!) {
                    socialPayment {
                        addPreviousBillingBalance(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateSocialPayment(input: SocialPaymentUpdateInput, frag: string) {
        return this.mutate<SocialPaymentObject>(
            `
                mutation($input: SocialPaymentUpdateInput!) {
                    socialPayment {
                        update(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteSocialPayment(input: IdInput, frag = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    socialPayment {
                        delete(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public closeSocialBillingDeficit(input: IdInput, frag: string = 'id') {
        return this.mutate<SocialPaymentObject>(
            `
                mutation($input: IdInput!) {
                    socialPayment {
                        closeBillingDeficit(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

}
